import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";

/**
 * Site-wide footer used at the bottom of authenticated module pages
 * (Dashboard, etc.). Mirrors the public footer design with IPOLabs branding,
 * three link columns and a small social/contact column.
 */
const AppFooter = ({ variant = "dark" }) => {
  const isNavy = variant === "navy";
  return (
    <footer
      className={`relative overflow-hidden text-white border-t ${isNavy ? "border-[#1c3257]" : "bg-black border-white/10"}`}
      style={isNavy ? { background: "linear-gradient(135deg,#0a1b34 0%,#0d2347 55%,#10294f 100%)" } : undefined}
      data-testid="app-footer"
    >
      {isNavy && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-[35%] -right-[6%] w-[460px] h-[460px]"
          style={{ background: "radial-gradient(circle,rgba(56,160,255,.22),transparent 70%)" }}
        />
      )}
      <div className="relative max-w-7xl mx-auto px-8 lg:px-16 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <img
              src="/ipolabs-logo-full.png"
              alt="IPOLabs"
              className="h-[120px] w-auto object-contain brightness-0 invert -mt-6 -ml-3"
            />
            <p className="text-white/55 text-sm leading-relaxed max-w-xs mt-4">
              India's first AI-powered IPO Operating System. End-to-end DRHP journey with privacy,
              expert marketplace, and funding solutions.
            </p>
          </div>

          {/* Platform */}
          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/90 font-semibold mb-6">Platform</h4>
            <ul className="space-y-5 text-sm text-white/70">
              <li><Link to="/ipo-navigator" className="hover:text-white transition-colors">IPO Navigator</Link></li>
              <li><Link to="/drhp-workspace" className="hover:text-white transition-colors">DRHP Workspace</Link></li>
              <li><Link to="/advisor-marketplace" className="hover:text-white transition-colors">Advisor Marketplace</Link></li>
              <li><Link to="/document-vault" className="hover:text-white transition-colors">Document Vault</Link></li>
              <li><Link to="/oracle" className="hover:text-white transition-colors">{"Oracle {IPO.GPT}"}</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="md:col-span-2">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/90 font-semibold mb-6">Company</h4>
            <ul className="space-y-5 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
              <li><Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link></li>
              <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
            </ul>
          </div>

          {/* Get in touch */}
          <div className="md:col-span-4">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-white/90 font-semibold mb-6">Get in touch</h4>
            <p className="text-sm text-white/70 mb-5">Mumbai, Maharashtra, India</p>
            <a
              href="mailto:founders.ipolabs@gmail.com"
              className="text-sm text-white/70 hover:text-white transition-colors block mb-6"
            >
              founders.ipolabs@gmail.com
            </a>
            <div className="flex items-center gap-3">
              <a
                href="https://www.linkedin.com/company/ipo-labs"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/15 transition-colors flex items-center justify-center"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-4 h-4 text-white" />
              </a>
              <a
                href="https://x.com/ipolabs"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="w-9 h-9 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/15 transition-colors flex items-center justify-center"
                data-testid="footer-x"
              >
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white" fill="currentColor" aria-hidden>
                  <path d="M18.244 2H21.5l-7.5 8.572L23 22h-6.844l-5.36-7.005L4.7 22H1.44l8.027-9.176L1 2h7.014l4.846 6.408L18.244 2zm-1.2 18h1.894L7.04 4H5.064l11.98 16z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <span>© {new Date().getFullYear()} IPOLabs Private Limited. All rights reserved.</span>
          <div className="flex items-center gap-7">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/disclaimer" className="hover:text-white transition-colors">SEBI Compliance</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AppFooter;
